/**
 * <TutorGoldenRules> — « Les 5 règles d'or du live », à lire dans l'app (organism).
 *
 * Remplace l'affiche de 2024 (une image du site, ouverte dans la visionneuse : illisible au
 * téléphone, figée sur l'ancien live — Wajih, 03/11). Les règles sont réécrites sur la formation
 * d'aujourd'hui (la vidéo et le quiz de validation) : le champion qui accueille l'élève, les modes Dialogue
 * et Concentré, les activités tirées du cours de l'élève, « Voir le rendu », le bilan par élève, la
 * demande d'absence. Une carte-liste, une ligne par règle : son picto, son titre numéroté, ses gestes.
 *
 * **Chaque règle a son picto**, comme sur l'ancienne affiche (Wajih, 05/11) : un glyphe dans une `<IconBubble>`.
 *
 * Sans données : le texte vit ici, comme les libellés d'un écran — sauf le nom du champion qui
 * prépare, qui arrive en prop (`helperName`) : un espace pourra avoir les siens. La feuille qui le pose
 * (la page Ressources, hors live comme dans la salle) en porte le titre. Il s'écrit avec des espaces
 * ordinaires : `frenchSpacing` rend insécables celles de la ponctuation au rendu.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListRow } from "../../molecules/CardList"
import { inkOnFill } from "../../theme/tokens/colors"
import { frenchSpacing } from "../../utils/frenchSpacing"

export const TUTOR_GOLDEN_RULES_TITLE = "Les 5 règles d'or du live"

type GoldenRule = { title: string; icon: IconName; points: string[] }

/**
 * Le texte validé dans la PR ; il suit la formation (vidéo et quiz) et la base de connaissance.
 * `helper` : le nom du champion qui prépare l'élève.
 */
const tutorGoldenRules = (helper: string): GoldenRule[] => [
    {
        title: "Un cadre pour faire cours",
        icon: "desktop",
        points: [
            "Un endroit calme et une bonne connexion.",
            "Un ordinateur ou une tablette posés sur une table : jamais un téléphone.",
            "Ta caméra allumée pendant tout le live : c'est elle qui tient l'attention des élèves.",
        ],
    },
    {
        title: "Un démarrage préparé",
        icon: "clock",
        points: [
            "Connecte-toi 5 minutes avant l'heure.",
            "Avant d'entrer, lis le dossier de chaque élève (touche son prénom), et toujours quand tu remplaces.",
            `${helper} a accueilli l'élève : ce qu'il veut travailler, ses contrôles, souvent une activité prête. Pars de là pour fixer avec lui l'objectif de la séance.`,
        ],
    },
    {
        title: "Le cours de l'élève, pas un cours de plus",
        icon: "camera",
        points: [
            "À chaque séance, demande une photo de son cours ou des exercices de son prof, pour ce soir et la prochaine fois.",
            `Avec cette photo, crée une activité : ${helper} en tire des exercices qui leur ressemblent. Ce que ${helper} ajoute, c'est toi qui décides de le garder.`,
            "Des activités courtes, de 10 à 15 minutes. Pour en changer, ouvre-la puis « Afficher à l'élève ».",
        ],
    },
    {
        title: "Un bon rythme entre les élèves",
        icon: "stopwatch",
        points: [
            "Passe de l'un à l'autre toutes les 5 minutes environ ; le chrono de sa carte dit depuis quand vous n'avez pas échangé.",
            "En mode Concentré, son micro est coupé : il n'entend personne et ne voit pas ton écran partagé. Repasse-le en Dialogue pour lui parler.",
            "Il rend son travail en photo : ouvre-le avec « Voir le rendu » et corrige-le avec lui, sur le fond et sur la forme.",
        ],
    },
    {
        title: "Une fin de live complète",
        icon: "flag",
        points: [
            "Un mot de conclusion avec chaque élève, et une courte activité à faire seul d'ici la prochaine séance.",
            "Pour chaque élève, une note et un commentaire, écrit ou dicté, avant de partir.",
            "Un live que tu ne pourras pas assurer ? Fais la demande d'absence dans l'app, le plus tôt possible.",
        ],
    },
]

/** La pastille du picto : teal plein dans les deux thèmes. */
const PICTO_FILL = "role-selected"

export type TutorGoldenRulesProps = {
    /** Le nom du champion qui prépare l'élève avant le live. */
    helperName: string
    testID?: string
}

export function TutorGoldenRules(props: TutorGoldenRulesProps) {
    return (
        <CardList testID={props.testID}>
            {tutorGoldenRules(props.helperName).map((rule, index) => (
                <CardListRow key={rule.title} py="4">
                    <Row gap="3" align="start">
                        <IconBubble
                            iconName={rule.icon}
                            iconSize={20}
                            color={PICTO_FILL}
                            iconColor={inkOnFill(PICTO_FILL)}
                        />
                        <Stack gap="2" style={styles.flex}>
                            <Text size="text-lg" weight="bold" color="text-strong" accessibilityRole="header">
                                {`${index + 1}. ${rule.title}`}
                            </Text>
                            {rule.points.map((point) => (
                                <Row key={point} gap="2" align="start">
                                    <View style={styles.check}>
                                        <Icon name="check" size={16} color="role-strong" />
                                    </View>
                                    <Text size="text-md" color="text-primary" style={styles.flex}>
                                        {frenchSpacing(point)}
                                    </Text>
                                </Row>
                            ))}
                        </Stack>
                    </Row>
                </CardListRow>
            ))}
        </CardList>
    )
}

const styles = StyleSheet.create(() => ({
    // La coche s'aligne sur la première ligne du texte.
    check: { paddingTop: 2 },
    flex: { flex: 1, flexShrink: 1 },
}))
